//
import { createSelector as createSelectorBase } from '@reduxjs/toolkit';
import { List as ImmutableList, Map as ImmutableMap, type RecordOf } from 'immutable';

import type { ACCOUNTS_REDUCER_KEY, accountsTypes } from 'app-components/flux/accounts';
import { accountsSelectors } from 'app-components/flux/accounts';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import type { FEATURE_FLAGS_REDUCER_KEY } from 'app-components/flux/feature-flags';
import type { INSTITUTION_LOGINS_REDUCER_KEY } from 'app-components/flux/institution-logins';
import { institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import type { INSTITUTIONS_REDUCER_KEY, institutionsTypes } from 'app-components/flux/institutions';
import { institutionsSelectors } from 'app-components/flux/institutions';
import type RootState from 'app-components/utils/redux-store/rootState';

import type { FundingAccount } from './types';
import { EligigibilityGroupEnum, FUNDING_ACCOUNTS_REDUCER_KEY } from './types';
import {
  isAccountEligibleForMicrodepositsUtil,
  isAccountEligibleForTransferringMoneyUtil,
  isAccountEligibleForTransfersUtil,
} from './utils';

type State = Pick<
  RootState,
  | typeof FUNDING_ACCOUNTS_REDUCER_KEY
  | typeof ACCOUNTS_REDUCER_KEY
  | typeof INSTITUTION_LOGINS_REDUCER_KEY
  | typeof INSTITUTIONS_REDUCER_KEY
  | typeof FEATURE_FLAGS_REDUCER_KEY
>;

const createSelector = createSelectorBase.withTypes<State>();

export const getFundingAccountsStore = (state: State) => state[FUNDING_ACCOUNTS_REDUCER_KEY];
export const getLoadPending = (state: State) => getFundingAccountsStore(state).loadPending;
export const getIsLoading = (state: State) => getFundingAccountsStore(state).isLoading;
export const getError = (state: State) => getFundingAccountsStore(state).error;
export const getLastSyncDate = (state: State) => getFundingAccountsStore(state).lastSyncDate;
export const getResourcesById = (state: State) => getFundingAccountsStore(state).resourcesById;
export const getFundingAccountsById = getResourcesById;
export const getFundingAccountById = (state: State, id?: string) => (id ? getResourcesById(state).get(id) : undefined);
export const getHasFundingAccounts = (state: State) => getResourcesById(state)?.size > 0;

// simple lookup from accountId
export const getFundingAccountFromAccountId = createSelector(
  [getFundingAccountsById, (_state, accountId?: string) => accountId],
  (fundingAccountsById, accountId) => fundingAccountsById.find((FA) => FA.linkedAccountId === accountId),
);

/* ===== Funding Account Behavior ===== */
/*
  NOTE: Right now we are using a fundingAccountId property in the loginAccount to connect fundingAccounts to loginAccounts. AccountId prop in fundingAccounts is just the ORIGINAL
        account that the fundingAccount was first connected to and NOT ALWAYS the active account it is currently connected to.

  (DWOLLA) FUND ACCT CREATION: When a funding account is created, it is saved in the DWOLLA database using the account number, it can never be removed by us from the DWOLLA database. If the loginAccount is ever deleted, and later re-added and the user
        attempts the setup process with that loginAccount with the same accountNumber, we automatically re-assign the fundingAccount on our end to the new loginAccount. fundingAccountId is added to the loginAccount.

  (DWOLLA) FUND ACCT DELETION: We **NEVER** delete DWOLLA accounts from datasets that are NOT test accounts, because if we delete them on our end, they will never again be able to be re-added because DWOLLA
        returns an error thinking we are trying to add a duplicate of an account that already exists on their end. (The funding account always exists on DWOLLAS end, so it must ALWAYS exist on our end)
        Test accounts can be deleted because we can change the account number to be whatever we want. fundingAccountId is removed from the loginAccount

  (DWOLLA) FUND ACCT MICRODEPOSITS: Users have to manually verify the microdeposits themselves. If we detect that a fundingAccount was made through dwolla, we expose the 'manual microdeposit verification' features to the user.

  (DWOLLA) ACCT DELETION: When a loginAccount is deleted, the fundingAccount remains for reasons explained in the (FUND ACCT DELETION) section. These fundingAccounts stay dormant, until the loginAccount is re-added to simplifi (Please see FUND ACCT CREATION for details)

  (DWOLLA) ACCT MADE MANUAL/CLOSED: When a loginAccount is made manual or closed, the fundingAccount remains for reasons explained in the (FUND ACCT DELETION) section. These fundingAccounts stay dormant, until the loginAccount is re-added to simplifi (Please see FUND ACCT CREATION for details)
        The fundingAccountId in the loginAccount is set to an empty string because if the loginAccount is re-added via reconnection to the institution, and the user re-enters the "setup funding account" flow, then there will be two loginAccount objects with 'fundingAccountId'
        that point to the same funding account!


  (INTUIT AND DWOLLA INTERACTION): Right now we are ONLY going to expose the INTUIT functionality to the users, however, the DWOLLA code still exists as a fallback just in case.


  (INTUIT) FUND ACCT CREATION: A funding account is created the moment the intuit widget is launched, with its verification status set to NONE. fundingAccountId is not yet added to the loginAccount until the process is fully completed.

  (INTUIT) FUND ACCT DELETION: fundingAccountId is removed from loginAccount

  (INTUIT) FUND ACCT MICRODEPOSITS: Users do not have access to manual verification, INTUIT handles it all on their end

  (INTUIT) ACCT DELETION: Nothing happens

  (INTUIT) ACCT MADE MANUAL/CLOSED: fundingAccountId is removed from loginAccount
*/

export const getAccountByFundingAccountId = createSelector(
  [(_state, fundingAccountId?: string) => fundingAccountId, accountsSelectors.getAccountsById],
  (fundingAccountId, accounts) =>
    fundingAccountId ? accounts.find((acc) => acc.fundingAccountId === fundingAccountId) : undefined,
);

// This returns an immutable object where the keys are the login accounts id and the value are the funding accounts
export const getAllFundingAccountsByAccountId = createSelector(
  [accountsSelectors.getAccountsById, getResourcesById],
  (accountsById, fundingAccountsById) =>
    ImmutableMap<string, FundingAccount>().withMutations((fundingAccountsByAccountId) => {
      accountsById.forEach((account, accountId) => {
        if (account?.fundingAccountId) {
          const fundingAccount = fundingAccountsById?.get(account.fundingAccountId);
          if (fundingAccount) {
            fundingAccountsByAccountId.set(accountId, fundingAccount);
          }
        }
      });
    }),
);

export const areNoAccountsEligible = createSelector(
  [
    (state) => institutionLoginsSelectors.getInstitutionLoginsById(state),
    (state) => accountsSelectors.getAccountsById(state),
    (state) => getAllFundingAccountsByAccountId(state),
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'a2AInvestmentAccounts'),
  ],
  (institutionLoginsById, accountsById, validFundingAccounts, includeInvestments) => {
    let isEmpty = true;
    accountsById.forEach((acc) => {
      if (acc?.id && acc.institutionLoginId) {
        const fundingAccount = validFundingAccounts.get(acc.id);
        const institution = institutionLoginsById.get(acc.institutionLoginId);
        if (isAccountEligibleForTransfersUtil(acc, fundingAccount, institution, !includeInvestments)?.status) {
          isEmpty = false;
          return false;
        }
      }
      return true;
    });
    return isEmpty;
  },
); // isAccountEligibleForTransfersUtil(acc, fundingAccount, institution)?.status

// This returns an immutable object where the keys are the login accounts id and the value are the funding accounts, only returns funding accounts that are verified! (NOTE: IT ONLY CHECKS IF THEY ARE VERIFIED, NOT IF THEY ARE ELIGIBLE
// AN ACCOUNT CAN BE VERIFIED, BUT BE IN AN ERROR STATE WHICH WOULD MAKE IT INELIGIBLE)
export const getVerifiedFundingAccountsByAccountId = createSelector(
  [
    accountsSelectors.getAccountsById,
    getResourcesById,
    (state) => institutionLoginsSelectors.getInstitutionLoginsById(state),
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'a2AInvestmentAccounts'),
  ],
  (accountsById, fundingAccountsById, institutionLogins, includeInvestments) =>
    ImmutableMap<string, FundingAccount>().withMutations((fundingAccountsByAccountId) => {
      accountsById.forEach((account, accountId) => {
        if (account?.fundingAccountId && account?.institutionLoginId) {
          const fundingAccount = fundingAccountsById?.get(account.fundingAccountId);
          const institutionLogin = institutionLogins?.get(account?.institutionLoginId);

          if (
            fundingAccount &&
            isAccountEligibleForTransferringMoneyUtil(account, fundingAccount, institutionLogin, !includeInvestments)
              ?.status
          ) {
            fundingAccountsByAccountId.set(accountId, fundingAccount);
          }
        }
      });
    }),
);

// This returns an immutable object where the keys are the login accounts id and the value are the funding accounts
export const getAllPendingAndVerifiedFundingAccountsByAccountId = createSelector(
  getAllFundingAccountsByAccountId,
  (fundingAccountsByAccountId) =>
    fundingAccountsByAccountId?.filter(
      (fundingAcc) =>
        fundingAcc.eligibility === EligigibilityGroupEnum.eligible ||
        fundingAcc.eligibility === EligigibilityGroupEnum.pendingVerification,
    ),
);

type statusString = 'VERIFIED_STATUS' | 'VERIFIED_ELIGIBLE' | 'PENDING_ELIGIBLE';
export const getFundingAccountsForStatus = createSelector(
  [
    (_state: unknown, status: statusString) => status,
    getFundingAccountsById,
    accountsSelectors.getAccountsById,
    institutionLoginsSelectors.getInstitutionLoginsById,
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'a2AInvestmentAccounts'),
  ],
  (status, fundingAccountsById, accountsById, loginsById, includeInvestments) =>
    fundingAccountsById.filter((fundingAcc) => {
      if (!status) {
        return true;
      }
      const acc = accountsById.get(fundingAcc.linkedAccountId || '');
      const institutionLogin = loginsById.get(acc?.institutionLoginId || '');
      switch (status) {
        case 'PENDING_ELIGIBLE':
          return isAccountEligibleForMicrodepositsUtil(acc, fundingAcc, institutionLogin, !includeInvestments)
            ?.isEligible;
        case 'VERIFIED_ELIGIBLE':
          return isAccountEligibleForTransferringMoneyUtil(acc, fundingAcc, institutionLogin, !includeInvestments)
            ?.isEligible;
        case 'VERIFIED_STATUS':
          return isAccountEligibleForTransferringMoneyUtil(acc, fundingAcc, institutionLogin, !includeInvestments)
            ?.status;
      }
    }),
);

export const isAccountEligibleForTransfers = createSelector(
  [
    (_state, account: accountsTypes.Account) => account,
    (state, account: accountsTypes.Account) => getFundingAccountById(state, account?.fundingAccountId),
    (state, account: accountsTypes.Account) =>
      institutionLoginsSelectors.getInstitutionLoginForId(state, account?.institutionLoginId as string),
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'a2AInvestmentAccounts'),
  ],
  (acc, fundingAcc, login, includeInvestments) =>
    isAccountEligibleForTransfersUtil(acc, fundingAcc, login, !includeInvestments),
);

export const isAccountEligibleForMicrodeposits = createSelector(
  [
    (_state, account: accountsTypes.Account) => account,
    (state, account: accountsTypes.Account) => getFundingAccountById(state, account?.fundingAccountId),
    (state, account: accountsTypes.Account) =>
      institutionLoginsSelectors.getInstitutionLoginForId(state, account?.institutionLoginId as string),
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'a2AInvestmentAccounts'),
  ],
  (acc, fundingAcc, login, includeInvestments) =>
    isAccountEligibleForMicrodepositsUtil(acc, fundingAcc, login, !includeInvestments),
);

export const isAccountEligibleForCashTransfer = createSelector(
  [
    (_state, account: accountsTypes.Account) => account,
    (state, account: accountsTypes.Account) => getFundingAccountById(state, account?.fundingAccountId),
    (state, account: accountsTypes.Account) =>
      institutionLoginsSelectors.getInstitutionLoginForId(state, account?.institutionLoginId as string),
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'a2AInvestmentAccounts'),
  ],
  (acc, fundingAcc, login, includeInvestments) =>
    isAccountEligibleForTransferringMoneyUtil(acc, fundingAcc, login, !includeInvestments),
);

export type InstitutionGrouping = {
  institution: RecordOf<institutionsTypes.Institution>;
  eligible: accountsTypes.Account[];
  enabled: accountsTypes.Account[];
  others: accountsTypes.Account[];
};
// returns an ImmutableList of grouping objects typed just above
export const getEligibleTransferAccountsList = createSelector(
  [
    accountsSelectors.getAccountsById,
    institutionLoginsSelectors.getInstitutionLoginsById,
    institutionsSelectors.getInstitutionsById,
    getFundingAccountsById,
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'a2AInvestmentAccounts'),
  ],
  (accountsById, loginsById, institutionsById, fundingAccounts, includeInvestments) => {
    const institutionMap = new Map<string, InstitutionGrouping>();

    accountsById.forEach((account) => {
      const login = account?.institutionLoginId && loginsById.get(account.institutionLoginId);
      if (!login) return;
      const institution = login?.institutionId && institutionsById.get(login.institutionId);
      if (!institution) return;
      const fundingAccount = account?.fundingAccountId ? fundingAccounts.get(account.fundingAccountId) : undefined;

      const existingObj = institutionMap.get(institution.id);
      const eligible = existingObj?.eligible || [];
      const enabled = existingObj?.enabled || [];
      const others = existingObj?.others || [];
      const microdepositResults = isAccountEligibleForMicrodepositsUtil(
        account,
        fundingAccount,
        login,
        !includeInvestments,
      );
      if (isAccountEligibleForTransferringMoneyUtil(account, fundingAccount, login, !includeInvestments).isEligible) {
        enabled.push(account);
      } else if (
        isAccountEligibleForTransfersUtil(account, fundingAccount, login, !includeInvestments).isEligible ||
        microdepositResults.isEligible ||
        microdepositResults.isPending ||
        microdepositResults.isFailure
      ) {
        eligible.push(account);
      } else {
        others.push(account);
      }
      institutionMap.set(institution.id, {
        institution: institution,
        eligible,
        enabled,
        others,
      });
    });
    return ImmutableList<InstitutionGrouping>(institutionMap.values()).sort(
      (a, b) =>
        b.eligible.length - a.eligible.length ||
        b.others.length - a.others.length ||
        b.enabled.length - a.enabled.length,
    );
  },
);
